"use client";

import { useMemo } from "react";
import ReactECharts from "echarts-for-react";
import { HEXACO_FACTORS, MOTIVATION_DIMS } from "@repo/pami";

/**
 * Une dimension du référentiel confrontée au score du candidat. Forme volontairement
 * MINIMALE et locale : ce paquet ne dépend pas de Convex, et le moteur de
 * comparaison (`convex/referentiel/comparer.ts`) produit exactement ces champs.
 */
export type LigneMatchingPami = {
  code: string;
  label: string;
  valeurCandidat: number;
  /**
   * Zone attendue par le référentiel et son poids (1, ou 0,5 pour une
   * dimension « important » d'un référentiel historique) — ou `sansZone` pour
   * une dimension sans attente particulière : elle n'a pas de zone à
   * dessiner, mais le score du candidat reste montré.
   */
  cible: { min: number; max: number; poids?: number } | { sansZone: true };
  ecart: number;
  statut:
    | "dans-la-cible"
    | "sous-la-cible"
    | "au-dessus-de-la-cible"
    | "sans-zone";
};

type Props = {
  lignes: LigneMatchingPami[];
  /** Bornes de l'échelle PAMI (0-10). */
  echelle: { min: number; max: number };
  titre?: string;
  hauteur?: string;
};

/** Couleur d'un point candidat selon sa position par rapport à la zone. Mêmes
 *  tokens que le reste de la restitution — jamais la couleur seule, le statut
 *  est aussi écrit dans l'infobulle. Gris neutre pour « sans zone » : ni bon ni
 *  mauvais, la dimension est simplement hors du calcul. */
const COULEUR_STATUT: Record<LigneMatchingPami["statut"], string> = {
  "dans-la-cible": "#16a34a",
  "sous-la-cible": "#2563eb",
  "au-dessus-de-la-cible": "#dc2626",
  "sans-zone": "#94a3b8",
};

const LIBELLE_STATUT: Record<LigneMatchingPami["statut"], string> = {
  "dans-la-cible": "Dans la zone attendue",
  "sous-la-cible": "Sous la zone attendue",
  "au-dessus-de-la-cible": "Au-dessus de la zone attendue",
  "sans-zone": "Pas d'attente particulière — hors du score",
};

/** Poids effectif d'une cible : 0 pour « sans zone » (pas d'attente
 *  particulière), la seule raison pour laquelle elle n'a pas de zone. */
function poidsDe(cible: LigneMatchingPami["cible"]): number | undefined {
  return "sansZone" in cible ? 0 : cible.poids;
}

/** Bornes de la zone, réduites à un point (largeur nulle, donc invisible) sur
 *  l'échelle quand la dimension n'a pas de zone. */
function borneMin(cible: LigneMatchingPami["cible"], echelleMin: number): number {
  return "sansZone" in cible ? echelleMin : cible.min;
}
function borneMax(cible: LigneMatchingPami["cible"], echelleMin: number): number {
  return "sansZone" in cible ? echelleMin : cible.max;
}

/** Ordre canonique des 20 traits — identique au graphique candidat
 *  (`PamiChart`), pour que les deux vues se superposent mentalement. Le domaine
 *  voyage avec, car c'est lui qui découpe le cercle en secteurs. */
const TRAITS_CANONIQUES: { code: string; domaine: string }[] = [
  ...HEXACO_FACTORS.flatMap((facteur) =>
    facteur.facets.map((facette) => ({
      code: facette.prefix,
      domaine: `${facteur.label} (${facteur.key})`,
    })),
  ),
  ...MOTIVATION_DIMS.map((dim) => ({
    code: dim.prefix,
    domaine: "Motivation",
  })),
];

const RANG_CANONIQUE = new Map(
  TRAITS_CANONIQUES.map((trait, index) => [trait.code, index]),
);
const META_CANONIQUE = new Map(
  TRAITS_CANONIQUES.map((trait) => [trait.code, trait]),
);

/**
 * Repli de la couleur `primary` du design system, en hexadécimal : l'équivalent
 * exact de `oklch(0.5338 0.2503 301.37)` (`packages/tailwind-config`). Il ne
 * sert qu'au rendu serveur et aux contextes sans CSS chargé — en navigateur,
 * c'est le token qui gagne, thème sombre compris.
 */
const PRIMARY_FALLBACK = "#8a2be2";

/**
 * Teinte UNIQUE de toutes les zones du référentiel : la `primary` du design
 * system, lue sur `--primary` pour suivre le thème plutôt que de le figer.
 *
 * Une seule teinte parce que la couleur ne code plus RIEN ici : le domaine est
 * nommé dans l'infobulle, le niveau attendu se lit à la distance au centre.
 * Garder six teintes ferait croire à une variable là où il n'y en a plus.
 */
function couleurZone(): string {
  if (typeof window === "undefined") return PRIMARY_FALLBACK;
  const valeur = getComputedStyle(document.documentElement)
    .getPropertyValue("--primary")
    .trim();
  return valeur || PRIMARY_FALLBACK;
}

/**
 * L'intensité de la zone code la PONDÉRATION de la dimension (poids 1 / 0,5 /
 * 0). Même variable que la taille du point, volontairement : ce qui pèse doit
 * s'imposer par la surface ET par le contraste, et ce qui pèse peu s'effacer
 * derrière sans disparaître. Le palier 0,5 n'existe que dans les référentiels
 * historiques à importance ; à attente unique, toutes les zones pèsent 1.
 */
function opaciteImportance(poids: number | undefined): number {
  if (poids === undefined) return 0.5;
  if (poids >= 1) return 0.85;
  if (poids >= 0.5) return 0.5;
  return 0.22;
}

/** Trait de séparation entre deux dimensions : pointillé léger et UNIFORME.
 *  La grille sépare, elle ne hiérarchise pas — c'est aux zones et aux points de
 *  porter le sens. */
const TRAIT_DIMENSION = "rgba(0,0,0,0.12)";

/**
 * Diamètre du point candidat selon le poids de la dimension (1 / 0,5 / 0 ; le
 * 0,5 n'existe que dans les référentiels historiques à importance).
 *
 * Troisième variable visuelle, sur un canal encore libre : la TAILLE dit
 * combien la dimension pèse dans le référentiel. Un écart sur une dimension
 * qui pèse doit sauter aux yeux avant un écart sur une dimension accessoire —
 * or la position seule ne hiérarchise rien.
 *
 * Diamètres et non aires : l'aire d'un disque croît en carré et exagérerait
 * l'écart entre les rangs.
 */
function tailleImportance(poids: number | undefined): number {
  if (poids === undefined) return 9;
  if (poids >= 1) return 14;
  if (poids >= 0.5) return 10;
  return 7;
}

/** Coupe un nom trop long sur 2 lignes au niveau d'un espace. */
function couperNom(nom: string, longueurMax = 14): string {
  if (nom.length <= longueurMax) return nom;
  const mots = nom.split(" ");
  if (mots.length < 2) return nom;
  let ligne1 = "";
  let i = 0;
  for (; i < mots.length; i++) {
    const suivant = (ligne1 + " " + mots[i]).trim();
    if (ligne1 && suivant.length > longueurMax) break;
    ligne1 = suivant;
  }
  const ligne2 = mots.slice(i).join(" ");
  return ligne2 ? `${ligne1}\n${ligne2}` : ligne1;
}

/**
 * Matching PAMI d'un candidat à son référentiel, en une seule figure.
 *
 * DEUX COUCHES SUPERPOSÉES, sur des géométries volontairement alignées :
 *
 * 1. Les **zones du référentiel**, en anneaux polaires. Chaque barre commence à
 *    `cible.min` et s'arrête à `cible.max` — elle ne part PAS du centre. C'est
 *    ce qui fait qu'« être dans la cible » se lit géométriquement : le point du
 *    candidat tombe dans l'anneau, ou il n'y tombe pas. Une barre partant du
 *    centre ne saurait pas dire qu'un score TROP HAUT est hors cible, ce qui est
 *    pourtant le cœur d'un référentiel en fourchette.
 * 2. Le **profil du candidat**, en radar : polygone fermé sans remplissage, pour
 *    ne pas masquer les anneaux, plus un point par trait coloré selon son statut.
 *
 * L'alignement des deux systèmes de coordonnées est obtenu en forçant le même
 * point de départ et le même sens de rotation (`startAngle: 90`, sens
 * trigonométrique) et en collant les catégories sur les axes
 * (`boundaryGap: false`). Sans ça, chaque point serait décalé d'un demi-secteur
 * par rapport à sa propre zone — et la figure mentirait.
 */
export function PamiMatchingChart({
  lignes,
  echelle,
  titre = "Matching au référentiel",
  hauteur = "clamp(420px, 62vw, 760px)",
}: Props) {
  const donnees = useMemo(() => {
    const triees = [...lignes].sort(
      (a, b) =>
        (RANG_CANONIQUE.get(a.code) ?? Number.MAX_SAFE_INTEGER) -
        (RANG_CANONIQUE.get(b.code) ?? Number.MAX_SAFE_INTEGER),
    );
    return triees.map((ligne) => {
      const meta = META_CANONIQUE.get(ligne.code);
      return {
        ligne,
        domaine: meta?.domaine ?? "Autre",
      };
    });
  }, [lignes]);

  const option = useMemo(() => {
    const categories = donnees.map((d) => d.ligne.label);
    /** Demi-largeur d'un secteur, en degrés : le décalage exact qui place un
     *  séparateur à mi-chemin entre deux dimensions voisines. */
    const demiSecteur = categories.length > 0 ? 180 / categories.length : 0;
    // Résolue au calcul de l'option, donc relue à chaque re-rendu : un
    // changement de thème repeint les zones sans code supplémentaire.
    const couleurPrimary = couleurZone();


    return {
      title: {
        text: titre,
        left: "center",
        top: 8,
        textStyle: { fontSize: 15, fontFamily: "sans-serif", fontWeight: 600 },
      },
      tooltip: {
        trigger: "item",
        confine: true,
        // Décalée du curseur et non-survolable : posée SOUS le pointeur, la
        // boîte volait le survol à l'anneau, qui la faisait disparaître, ce qui
        // rendait le survol à l'anneau — d'où un clignotement en boucle.
        enterable: false,
        position: (point: [number, number]) => [point[0] + 16, point[1] + 16],
        // Aucune transition : entre deux anneaux voisins, l'interpolation de
        // position relançait le même cycle.
        transitionDuration: 0,
        // Une seule infobulle, portée par les anneaux : elle dit TOUT ce qui
        // concerne la dimension survolée — zone, score, écart —
        // plutôt que d'éclater l'information entre deux couches.
        formatter: (params: { dataIndex: number; seriesId?: string }) => {
          const entree = donnees[params.dataIndex];
          if (!entree) return "";
          const { ligne } = entree;
          const ecart =
            ligne.ecart === 0
              ? "aucun"
              : `${Math.round(ligne.ecart * 10) / 10} pt${
                  Math.abs(ligne.ecart) > 1 ? "s" : ""
                }`;
          return [
            `<b>${ligne.label}</b>`,
            `Domaine : ${entree.domaine}`,
            "sansZone" in ligne.cible
              ? null
              : `Zone attendue : ${ligne.cible.min} – ${ligne.cible.max}`,
            `Score du candidat : ${Math.round(ligne.valeurCandidat * 10) / 10}/${echelle.max}`,
            "sansZone" in ligne.cible
              ? LIBELLE_STATUT["sans-zone"]
              : `${LIBELLE_STATUT[ligne.statut]} · écart ${ecart}`,
          ]
            .filter(Boolean)
            .join("<br/>");
        },
      },
      // Pas de légende : toutes les zones partagent la même teinte, il n'y a
      // donc plus aucune couleur à décoder. Le domaine est marqué par les
      // séparateurs renforcés et nommé dans l'infobulle.
      legend: { show: false },
      toolbox: { show: true, feature: { saveAsImage: { show: true } } },

      // ── Couche 1 : zones du référentiel ────────────────────────────────
      polar: [
        { radius: ["0%", "62%"], center: ["50%", "52%"] },
        // Polaire jumeau, dédié aux SÉPARATEURS de dimensions. Il ne porte
        // aucune donnée : son seul rôle est de fournir des `splitLine`
        // décalées d'un demi-secteur (`startAngle` + `demiSecteur`), donc
        // tracées ENTRE deux dimensions et non au travers de l'une d'elles.
        { radius: ["0%", "62%"], center: ["50%", "52%"] },
      ],
      angleAxis: [
        {
          type: "category",
          data: categories,
          // Collé à l'axe + sens trigonométrique + départ à 90° : c'est CE triplet
          // qui aligne les anneaux sur les sommets du radar.
          boundaryGap: false,
          clockwise: false,
          startAngle: 90,
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: { show: false },
          splitLine: { show: false },
        },
        {
          polarIndex: 1,
          // Axe NUMÉRIQUE 0–360 et non catégoriel : un axe de catégories en
          // `boundaryGap: false` s'étend de la catégorie 0 à la N-1, soit
          // (N-1)/N du tour — il manque un secteur, et TOUT ce que l'axe trace
          // (cercles concentriques compris) s'y interrompt. Un axe en degrés
          // couvre le cercle entier ; un tick tous les `2 × demiSecteur` retombe
          // exactement entre deux dimensions.
          type: "value",
          min: 0,
          max: 360,
          interval: demiSecteur * 2,
          clockwise: false,
          startAngle: 90 + demiSecteur,
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: { show: false },
          splitLine: {
            show: true,
            lineStyle: { type: "dashed", color: TRAIT_DIMENSION, width: 1 },
          },
          z: 1,
        },
      ],
      radiusAxis: [
        {
          type: "value",
          min: echelle.min,
          max: echelle.max,
          splitNumber: 5,
          // Aucun cercle ici : ceux de ce polaire seraient tronqués du même
          // secteur manquant. Ils sont tracés par le polaire 1, dont l'axe
          // angulaire fait le tour complet.
          splitLine: { show: false },
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: { show: false },
        },
        {
          polarIndex: 1,
          type: "value",
          min: echelle.min,
          max: echelle.max,
          splitNumber: 5,
          z: 1,
          splitLine: {
            show: true,
            lineStyle: { type: "dotted", color: "rgba(0,0,0,0.15)" },
          },
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: { show: false },
        },
      ],

      // ── Couche 2 : radar du candidat ───────────────────────────────────
      radar: {
        center: ["50%", "52%"],
        radius: "62%",
        startAngle: 90,
        shape: "polygon",
        splitNumber: 5,
        indicator: donnees.map((d) => ({
          name: couperNom(d.ligne.label),
          min: echelle.min,
          max: echelle.max,
        })),
        // La grille est déjà tracée par le système polaire : la redoubler
        // rendrait la figure illisible. Le radar n'apporte que les noms.
        axisLine: { show: false },
        splitLine: { show: false },
        splitArea: { show: false },
        axisName: {
          fontSize: 11,
          fontFamily: "sans-serif",
          lineHeight: 13,
          color: "#334155",
        },
      },

      series: [
        {
          // Série vide rattachée au polaire jumeau : ECharts ne rend un système
          // de coordonnées que s'il porte au moins une série. Elle ne dessine
          // rien — seules ses `splitLine` d'axe sont visibles.
          name: "separateurs",
          type: "scatter",
          coordinateSystem: "polar",
          polarIndex: 1,
          silent: true,
          symbolSize: 0,
          tooltip: { show: false },
          data: [],
          z: 1,
        },
        {
          // Socle invisible : porte la barre jusqu'à `min` sans rien dessiner.
          // C'est ce qui transforme une barre en ANNEAU. Pour une dimension
          // « sans zone », min = max = échelle.min : la barre a une hauteur
          // nulle, donc aucun anneau ne se dessine — seul le point du candidat
          // reste visible sur cette dimension.
          name: "socle",
          type: "bar",
          coordinateSystem: "polar",
          stack: "zone",
          silent: true,
          itemStyle: { color: "transparent" },
          tooltip: { show: false },
          data: donnees.map(
            (d) => borneMin(d.ligne.cible, echelle.min) - echelle.min,
          ),
          z: 2,
        },
        {
          name: "Zone attendue",
          type: "bar",
          coordinateSystem: "polar",
          stack: "zone",
          barCategoryGap: "18%",
          itemStyle: {
            borderColor: "#fff",
            borderWidth: 1,
          },
          // L'`emphasis` d'ECharts éclaircit l'anneau au survol : sur une série
          // dont l'opacité CODE la pondération, ce repeint ferait lire un autre
          // poids. L'infobulle suffit à signaler ce qui est survolé.
          emphasis: { disabled: true },
          data: donnees.map((d) => ({
            value:
              borneMax(d.ligne.cible, echelle.min) -
              borneMin(d.ligne.cible, echelle.min),
            itemStyle: {
              color: couleurPrimary,
              opacity: opaciteImportance(poidsDe(d.ligne.cible)),
            },
          })),
          z: 2,
        },
        {
          name: "Profil du candidat",
          type: "radar",
          symbol: "none",
          lineStyle: { width: 2, color: "#0f172a" },
          // Pas d'`areaStyle` : un remplissage masquerait les zones en dessous,
          // qui sont le référentiel lui-même.
          tooltip: { show: false },
          data: [{ value: donnees.map((d) => d.ligne.valeurCandidat) }],
          z: 5,
        },
        {
          // Points du candidat en scatter POLAIRE et non en symboles du radar :
          // seul le scatter accepte une couleur par point, et c'est elle qui
          // porte le statut (dans / sous / au-dessus de la zone).
          name: "Score du candidat",
          type: "scatter",
          coordinateSystem: "polar",
          data: donnees.map((d, index) => ({
            value: [d.ligne.valeurCandidat, index],
            symbolSize: tailleImportance(poidsDe(d.ligne.cible)),
            itemStyle: {
              color: COULEUR_STATUT[d.ligne.statut],
              borderColor: "#fff",
              borderWidth: 1.5,
            },
          })),
          z: 6,
        },
      ],
    };
  }, [donnees, echelle, titre]);

  if (donnees.length === 0) {
    return (
      <p className="text-muted-foreground py-8 text-center text-sm">
        Aucune dimension commune entre ce référentiel et la passation du
        candidat : rien à représenter.
      </p>
    );
  }

  return (
    <ReactECharts
      option={option}
      notMerge
      style={{ width: "100%", height: hauteur }}
    />
  );
}
